/**
 * WaitlistDemandOverlay — public waitlist demand for the coverage
 * map (Part G, 2026-07-21).
 *
 * PRIVACY INVARIANTS (Guard 14 + 15):
 *   - Reads ONLY the aggregate endpoint `/api/public/waitlist/demand`.
 *   - NEVER renders individual pins or PII of any kind.
 *   - Small-count ZIP suppression is enforced at the ENDPOINT (Guard 15);
 *     this component doesn't apply its own threshold — trusts the API.
 *
 * GRACEFUL DEGRADATION:
 *   - Rendered as a labeled list beside the map (NOT overlaid as
 *     coordinate pins). Works with or without a Mapbox token —
 *     when the token is missing (see Item 8b), this list is the
 *     entire demand surface.
 */
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function WaitlistDemandOverlay() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/public/waitlist/demand")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData({ by_zip: [], by_metro: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-2xl border border-border bg-pp-surface p-6 text-center"
        data-testid="waitlist-demand-loading"
      >
        <Loader2 className="mx-auto h-4 w-4 animate-spin text-brand-orange" />
      </div>
    );
  }

  const zips = data?.by_zip || [];
  const metros = data?.by_metro || [];
  const totalMetro = metros.reduce((a, m) => a + (m.count || 0), 0);
  const threshold = Number(data?.small_count_threshold || 5);

  return (
    <div
      className="rounded-2xl border border-border bg-pp-surface p-5"
      data-testid="waitlist-demand-overlay"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-heading text-base font-bold text-pp-text">
          <span className="text-brand-orange">Waitlist demand</span> by area
        </h3>
        <span
          className="text-xs text-muted-foreground"
          data-testid="waitlist-demand-total"
        >
          {totalMetro} total
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Aggregate counts only — ZIPs with fewer than {threshold} on the list are
        rolled into their metro total (not shown individually) to protect
        privacy.
      </p>

      {metros.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase text-muted-foreground">By metro</div>
          <ul className="mt-1 space-y-1" data-testid="waitlist-demand-metros">
            {metros.map((m) => (
              <li
                key={m.metro}
                className="flex justify-between text-sm text-pp-text"
                data-testid={`waitlist-demand-metro-${m.metro}`}
              >
                <span>{m.metro}</span>
                <span className="font-semibold">{m.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {zips.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase text-muted-foreground">
            By ZIP ({threshold}+ waiting)
          </div>
          <ul className="mt-1 space-y-1" data-testid="waitlist-demand-zips">
            {zips.map((z) => (
              <li
                key={z.zip}
                className="flex justify-between text-sm text-pp-text"
                data-testid={`waitlist-demand-zip-${z.zip}`}
              >
                <span>{z.zip}</span>
                <span className="font-semibold">{z.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {metros.length === 0 && zips.length === 0 && (
        <p
          className="mt-4 text-sm text-muted-foreground"
          data-testid="waitlist-demand-empty"
        >
          No signups yet. Once the waitlist starts filling up, the counts show
          up here.
        </p>
      )}
    </div>
  );
}
